// Inline validator helpers (previously in validators.js)
const validators = {
  required: (message = "This field is required") => ({ required: message }),
  digitsDotSpaceHyphen: (
    message = "Only digits, dot, space and hyphen are allowed"
  ) => ({
    pattern: { value: /^[0-9.\-\s]+$/, message },
  }),
  alphaNumNoSpace: (
    message = "Only alphanumeric characters without spaces are allowed"
  ) => ({
    pattern: { value: /^[A-Za-z0-9]+$/, message },
  }),
  loginId: (
    message = "Only alphanumeric characters and _ allowed. Space not allowed"
  ) => ({
    pattern: { value: /^[A-Za-z0-9_]+$/, message },
  }),
  alphaNumWithSpace: (
    message = "Only alphanumeric characters and spaces are allowed"
  ) => ({
    pattern: { value: /^[A-Za-z0-9 ]+$/, message },
  }),
  integerOnly: (message = "Only integer values are allowed") => ({
    pattern: { value: /^-?\d+$/, message },
  }),
  money: (decimalPlaces = 2, message) => {
    const msg = message || `Invalid amount (max ${decimalPlaces} decimal places)`;
    const regex = new RegExp("^-?\\d+(?:\\.\\d{0," + decimalPlaces + "})?$");
    return { pattern: { value: regex, message: msg } };
  },
  emailId: (message = "Invalid email address") => ({
    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message },
  }),
  mobileNo10Digit: (message = "Invalid mobile number (must be 10 digits)") => ({
    pattern: { value: /^[0-9]{10}$/, message },
  }),
  maxLength: (len, message) => ({ maxLength: { value: len, message } }),
  minLength: (len, message) => ({ minLength: { value: len, message } }),
  min: (val, message) => ({ min: { value: val, message } }),
  max: (val, message) => ({ max: { value: val, message } }),
  compose: (rulesArray = []) => Object.assign({}, ...rulesArray),
};

// Returns { isValid: boolean, errors: { [field]: message } }
export function validateMemberForm(form = {}) {
  const errors = {};
  const isTrulyEmpty = (val) => {
    if (val === undefined || val === null) return true;
    if (typeof val === 'string') return val.trim() === '';
    if (Array.isArray(val)) return val.length === 0;
    return false;
  };

  const rules = {
    applicationId: validators.compose([
    //   validators.maxLength(30, "Application Id must be ≤ 30 chars"),
    ]),
    applicationDate: validators.compose([
      validators.required("Application Date is required"),
    ]),
    memberCode: validators.compose([
      validators.required("Member Number is required"),
    //   validators.alphaNumNoSpace("Only letters/numbers allowed for Member Number"),
    //   validators.maxLength(20, "Member Number must be ≤ 20 chars"),
    ]),
    regDate: validators.compose([
      validators.required("Registration Date is required"),
    ]),
    nameTitle: validators.compose([
      validators.required("Title is required"),
    ]),
    name: validators.compose([
      validators.required("Name is required"),
      validators.alphaNumWithSpace("Only letters/numbers and spaces in Name"),
      validators.maxLength(120, "Name must be ≤ 120 chars"),
    ]),

    gender: validators.compose([
      validators.required("Gender is required"),
    ]),
    age: validators.compose([
      validators.integerOnly("Age must be an integer"),
      validators.min(0, "Age cannot be negative"),
      validators.max(120, "Age seems invalid (max 120)"),
    ]),
    email: validators.compose([
      validators.emailId("Invalid email address"),
    ]),
    mobile: validators.compose([
      validators.mobileNo10Digit("Mobile must be 10 digits"),
    ]),

    maritalStatus: validators.compose([]),
    marriageDate: validators.compose([]), // will be conditionally required

    // Bank and KYC (optional but validate if present)
    bankAccountNo: validators.compose([
      validators.digitsDotSpaceHyphen("Only digits, dot, space and hyphen allowed"),
      validators.maxLength(20, "Bank A/C No must be ≤ 20 chars"),
    ]),
    ifscCode: validators.compose([
      validators.alphaNumNoSpace("Only letters and numbers allowed"),
      validators.maxLength(15, "IFSC must be ≤ 15 chars"),
    ]),
    aadharNumber: validators.compose([
      validators.maxLength(12, "Aadhaar should be 12 digits"),
      validators.minLength(12, "Aadhaar should be 12 digits"),
      validators.digitsDotSpaceHyphen("Only digits allowed in Aadhaar"),
    ]),
    panNumber: validators.compose([
      validators.alphaNumNoSpace("Only letters/numbers allowed in PAN"),
      validators.maxLength(10, "PAN should be 10 chars"),
      validators.minLength(10, "PAN should be 10 chars"),
    ]),

    // Father and relation fields
    fatherName: validators.compose([
      validators.required("Father Name is required"),
      validators.alphaNumWithSpace("Only letters/numbers and spaces in Father Name"),
      validators.maxLength(120, "Father Name must be ≤ 120 chars"),
    ]),
    fatherTitle: validators.compose([
      validators.required("Father Title is required"),
    ]),
    soWoCo: validators.compose([
      validators.required("Relation (So/Wo/Co) is required"),
    ]),

    // Dates (basic presence/format handled by UI; here presence for key ones if given)
    dob: validators.compose([]),

    // Office Details
    employeeId: validators.compose([
      validators.alphaNumNoSpace("Only letters/numbers allowed in Employee Id"),
      validators.maxLength(30, "Employee Id must be ≤ 30 chars"),
    ]),
    companyName: validators.compose([
      validators.alphaNumWithSpace("Only letters/numbers and spaces in Company Name"),
      validators.maxLength(120, "Company Name must be ≤ 120 chars"),
    ]),
    department: validators.compose([
      validators.alphaNumWithSpace("Only letters/numbers and spaces in Department"),
      validators.maxLength(80, "Department must be ≤ 80 chars"),
    ]),
    supervisor: validators.compose([
      validators.alphaNumWithSpace("Only letters/numbers and spaces in Supervisor"),
      validators.maxLength(80, "Supervisor must be ≤ 80 chars"),
    ]),
    headOfficeAddress: validators.compose([
      validators.maxLength(250, "Head Office Address must be ≤ 250 chars"),
    ]),
    officePostingAddress: validators.compose([
      validators.maxLength(250, "Office Posting Address must be ≤ 250 chars"),
    ]),
    telephoneNo: validators.compose([
      validators.digitsDotSpaceHyphen("Only digits, space, dot or hyphen in Telephone"),
      validators.maxLength(20, "Telephone must be ≤ 20 chars"),
    ]),
    reportingManager: validators.compose(),
    income: validators.compose([
      validators.money(2, "Income must be a valid amount"),
    ]),
    pfGpfNo: validators.compose([
      validators.alphaNumNoSpace("Only letters/numbers allowed in PF/GPF No"),
      validators.maxLength(20, "PF/GPF No must be ≤ 20 chars"),
    ]),
    officeJoiningDate: validators.compose([]),

    // KYC Dates
    kycVerificationDate: validators.compose([]),

    // Additional KYC IDs
    nsoNumber: validators.compose([
      validators.alphaNumNoSpace("Only letters/numbers allowed in Passport No"),
      validators.maxLength(20, "Passport No must be ≤ 20 chars"),
    ]),
    voterRationCardNo: validators.compose([
      validators.alphaNumNoSpace("Only letters/numbers allowed in Voter/Ration Card"),
      validators.maxLength(20, "Voter/Ration Card must be ≤ 20 chars"),
    ]),
    passportDlNo: validators.compose([
      validators.alphaNumNoSpace("Only letters/numbers allowed in Passport/DL No"),
      validators.maxLength(20, "Passport/DL No must be ≤ 20 chars"),
    ]),
    bankName: validators.compose([
      validators.alphaNumWithSpace("Only letters/numbers and spaces in Bank Name"),
      validators.maxLength(120, "Bank Name must be ≤ 120 chars"),
    ]),
    bankBranch: validators.compose([
      validators.alphaNumWithSpace("Only letters/numbers and spaces in Bank Branch"),
      validators.maxLength(120, "Bank Branch must be ≤ 120 chars"),
    ]),
    bankManager: validators.compose([
      validators.alphaNumWithSpace("Only letters/numbers and spaces in Bank Manager"),
      validators.maxLength(120, "Bank Manager must be ≤ 120 chars"),
    ]),
    bankAddress: validators.compose([
      validators.maxLength(250, "Bank Address must be ≤ 250 chars"),
    ]),
    ifscCodeAlt: validators.compose([]),

    // Agent & Leads
    agentCode: validators.compose([
    ]),
    agentName: validators.compose([
    ]),
    leadDate: validators.compose([]),

    // Nominee Details
    nomineeName: validators.compose([
      validators.alphaNumWithSpace("Only letters/numbers and spaces in Nominee Name"),
      validators.maxLength(120, "Nominee Name must be ≤ 120 chars"),
    ]),
    nomineeTitle: validators.compose([]),
    nomineeRelation: validators.compose([]),
    nomineeOccupation: validators.compose([
      validators.alphaNumWithSpace("Only letters/numbers and spaces in Nominee Occupation"),
      validators.maxLength(80, "Nominee Occupation must be ≤ 80 chars"),
    ]),
    nomineeMobileNo: validators.compose([
      validators.mobileNo10Digit("Nominee Mobile must be 10 digits"),
    ]),
    nomineeDob: validators.compose([]),
    nomineeAddress: validators.compose([
      validators.maxLength(250, "Nominee Address must be ≤ 250 chars"),
    ]),
    nomineeAadharNumber: validators.compose([
      validators.maxLength(12, "Nominee Aadhaar should be 12 digits"),
      validators.minLength(12, "Nominee Aadhaar should be 12 digits"),
      validators.digitsDotSpaceHyphen("Only digits allowed in Nominee Aadhaar"),
    ]),
    nomineePanNumber: validators.compose([
      validators.alphaNumNoSpace("Only letters/numbers allowed in Nominee PAN"),
      validators.maxLength(10, "Nominee PAN should be 10 chars"),
      validators.minLength(10, "Nominee PAN should be 10 chars"),
    ]),
  };

  // Evaluate a single field against composed rules
  const evaluate = (value, rule) => {
    if (!rule) return null;
    // Required check
    if (rule.required) {
      const empty = value === undefined || value === null || String(value).trim() === "";
      if (empty) return rule.required;
    }
    // Pattern check
    if (rule.pattern && rule.pattern.value instanceof RegExp) {
      if (value !== undefined && value !== null && String(value) !== "") {
        if (!rule.pattern.value.test(String(value))) return rule.pattern.message;
      }
    }
    // Length checks - only validate if field has a value
    if (rule.minLength && rule.minLength.value != null) {
      if (value !== undefined && value !== null && String(value).trim() !== "" && String(value).length < rule.minLength.value) {
        return rule.minLength.message;
      }
    }
    if (rule.maxLength && rule.maxLength.value != null) {
      if (value !== undefined && value !== null && String(value).trim() !== "" && String(value).length > rule.maxLength.value) {
        return rule.maxLength.message;
      }
    }
    // Numeric range checks
    if (rule.min && rule.min.value != null) {
      const num = Number(value);
      if (!Number.isNaN(num) && num < rule.min.value) return rule.min.message;
    }
    if (rule.max && rule.max.value != null) {
      const num = Number(value);
      if (!Number.isNaN(num) && num > rule.max.value) return rule.max.message;
    }
    return null;
  };

  // Run validations
  Object.entries(rules).forEach(([field, rule]) => {
    const message = evaluate(form[field], rule);
    if (message) errors[field] = message;
  });

  // Conditional validations
  if (
    typeof form.maritalStatus === "string" &&
    form.maritalStatus.trim().toLowerCase() === "married"
  ) {
    if (!form.marriageDate || String(form.marriageDate).trim() === "") {
      errors.marriageDate = "Marriage Date is required when Marital Status is Married";
    }
  }

  // Nominee conditional: if nomineeName present, relation required
  if (form.nomineeName && String(form.nomineeName).trim() !== "") {
    if (!form.nomineeRelation || String(form.nomineeRelation).trim() === "") {
      errors.nomineeRelation = "Nominee Relation is required when Nominee Name is provided";
    }
  }

  // Address validations - only validate format if fields have values (addresses are optional)
  const firstAddress = Array.isArray(form.addresses) ? form.addresses[0] : null;
  if (firstAddress) {
    // Only validate format if pincode is provided
    if (firstAddress.pincode && String(firstAddress.pincode).trim() !== "") {
      const pinOk = /^\d{6}$/.test(String(firstAddress.pincode));
      if (!pinOk) errors["addresses.0.pincode"] = "Pincode must be 6 digits";
    }
    // Only validate format if contact number is provided
    if (firstAddress.contactNo && String(firstAddress.contactNo).trim() !== "") {
      const telOk = /^\d{10}$/.test(String(firstAddress.contactNo).replace(/\D/g, ""));
      if (!telOk) errors["addresses.0.contactNo"] = "Address Contact must be 10 digits";
    }
  }

  // Only validate required fields: memberCode, applicationDate, regDate, nameTitle, name, gender, fatherTitle, fatherName, soWoCo
  const requiredFields = ['memberCode', 'applicationDate', 'regDate', 'nameTitle', 'name', 'gender', 'fatherTitle', 'fatherName', 'soWoCo'];
  
  requiredFields.forEach(key => {
    const value = form[key];
    if (isTrulyEmpty(value)) {
      errors[key] = errors[key] || "This field is required";
    }
  });

  // Addresses are optional - no validation required

  return { isValid: Object.keys(errors).length === 0, errors };
}

export default validateMemberForm;


